import { useEffect, useState } from "react";
import { api, type Conference, type ReviewAssignmentWorkspace } from "../../api.js";
import { apiErrorMessage } from "../../../api/client.js";
import { Button } from "../../../design/components/Button.js";
import { Alert, EmptyState, Skeleton } from "../../../design/components/Feedback.js";
import { AssignmentStudio } from "./AssignmentStudio.js";

export function AssignmentsView({ conference, onWorkspace }: { conference: Conference; onWorkspace: (value: ReviewAssignmentWorkspace) => void }) {
  const [workspace, setWorkspace] = useState<ReviewAssignmentWorkspace | null>(null);
  const [selectedSubmission, setSelectedSubmission] = useState("");
  const [selectedReviewer, setSelectedReviewer] = useState("");
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [busy, setBusy] = useState("");

  const load = async (submissionId?: string) => {
    setError("");
    try {
      const result = await api.reviewAssignmentWorkspace(conference.id, conference.organization_id, submissionId);
      setWorkspace(result);
      onWorkspace(result);
    } catch (reason) {
      setError(apiErrorMessage(reason, "Assignments could not be loaded."));
    }
  };

  useEffect(() => { void load(); }, [conference.id, conference.organization_id]);

  const selectSubmission = (submissionId: string) => {
    setSelectedSubmission(submissionId);
    void load(submissionId || undefined);
  };

  const candidate = workspace?.suggestions.find((item) => item.reviewer_id === selectedReviewer);

  const assign = async () => {
    if (!workspace || !candidate || !selectedSubmission || !workspace.review_rounds[0]) return;
    setBusy(candidate.invitation_id);
    setError("");
    try {
      await api.assignReviewer(conference.id, conference.organization_id, {
        submission_id: selectedSubmission,
        reviewer_id: candidate.reviewer_id,
        invitation_id: candidate.invitation_id,
        review_round_id: workspace.review_rounds[0].id,
      });
      await load(selectedSubmission);
    } catch (reason) {
      setError(apiErrorMessage(reason, "The assignment could not be applied."));
    } finally {
      setBusy("");
    }
  };

  const createSyntheticKit = async () => {
    const accepted = workspace?.accepted_reviewers.find((item) => item.reviewer_id === selectedReviewer);
    if (!accepted) return;
    setBusy("synthetic-kit");
    setError("");
    setNotice("");
    try {
      const result = await api.createSyntheticReviewKit(conference.id, conference.organization_id, {
        reviewer_id: accepted.reviewer_id,
        invitation_id: accepted.invitation_id,
      });
      setNotice(result.assignments_created
        ? `${result.assignments_created} sample review assignments are ready.`
        : "The sample review assignments are already ready.");
      await load();
    } catch (reason) {
      setError(apiErrorMessage(reason, "Sample review assignments could not be created."));
    } finally {
      setBusy("");
    }
  };

  if (error && !workspace) return <Alert tone="danger">{error}</Alert>;
  if (!workspace) return <div aria-label="Loading assignments"><Skeleton /><Skeleton /></div>;

  if (workspace.assignment_mode === "STUDIO") return <AssignmentStudio conference={conference} onUpdated={() => void load()} />;

  return <div className="review-assignment-view">
    {error ? <Alert tone="danger">{error}</Alert> : null}
    {notice ? <Alert tone="success">{notice}</Alert> : null}
    <div className="review-assignment-controls">
      <label>Choose a submission<select value={selectedSubmission} onChange={(event) => selectSubmission(event.target.value)}><option value="">Select a submission</option>{workspace.submissions.map((submission) => <option key={submission.id} value={submission.id}>{submission.reference} · {submission.title}</option>)}</select></label>
      <label>Accepted reviewer<select value={selectedReviewer} onChange={(event) => setSelectedReviewer(event.target.value)}><option value="">Select an accepted reviewer</option>{workspace.accepted_reviewers.map((reviewer) => <option key={reviewer.invitation_id} value={reviewer.reviewer_id}>{reviewer.name || reviewer.email} · {reviewer.active_assignments}/{reviewer.expected_work} active</option>)}</select></label>
      <Button busy={Boolean(candidate && busy === candidate.invitation_id)} disabled={!selectedSubmission || !candidate || !workspace.review_rounds[0]} onClick={() => void assign()}>Assign reviewer</Button>
    </div>
    {workspace.synthetic_kit_available && !workspace.submissions.length ? <section className="review-synthetic-kit">
      <div><strong>Try the complete review workflow</strong><span>Create three clearly labelled fictional papers, a published rubric, and real assignments for the accepted reviewer selected above.</span></div>
      <Button busy={busy === "synthetic-kit"} disabled={!selectedReviewer} onClick={() => void createSyntheticKit()}>Add sample review assignments</Button>
    </section> : null}
    {workspace.assignments.length ? <table><thead><tr><th>Submission</th><th>Reviewer</th><th>Round</th><th>Status</th></tr></thead><tbody>{workspace.assignments.map((assignment) => <tr key={assignment.id}><td><strong>{assignment.submission_title}</strong><span>{assignment.submission_reference}</span></td><td>{assignment.reviewer_email}</td><td>{assignment.review_round_name}</td><td>{assignment.state}</td></tr>)}</tbody></table> : <EmptyState title="No assignments yet" description="Choose a submission, review eligible people and apply an assignment deliberately." />}
    {!workspace.accepted_reviewers.length ? <Alert tone="neutral">No accepted reviewers are ready yet. Their invitation must be accepted before you can assign work.</Alert> : selectedSubmission && !workspace.suggestions.length ? <Alert tone="neutral">No conflict-free accepted reviewers are available for this submission.</Alert> : selectedSubmission && selectedReviewer && !candidate ? <Alert tone="neutral">This reviewer cannot be assigned to the selected submission because of scope, conflict, or an existing assignment.</Alert> : candidate ? <p className="review-assignment-reason">{candidate.reason}</p> : null}
  </div>;
}
